/**
 * วิธีเรียงรายชื่อผู้ผ่านเกณฑ์
 *   'criteria'  เข้าเกณฑ์มากที่สุดก่อน (ค่าตั้งต้นของเซิร์ฟเวอร์)
 *   'seniority' ลำดับอาวุโสตามหลักเกณฑ์ ก.พ.
 */
export type CandidateOrder = 'criteria' | 'seniority';

import type {
  AuthorityMatrix,
  CandidateComparison,
  ExecutiveRoster,
  ExecutiveTree,
  ExecutivePositionsView,
  CareerGroup,
  CareerGroupOverview,
  CareerCoverage,
  CareerPathForPerson,
  PositionCareerPath,
  EligibilityReport,
  OrgNode,
  Paged,
  Position,
  RetirementBucket,
  RetirementHeatmap,
  RetirementRoster,
  RotationPlan,
  SeniorityList,
  VacancyList,
  RawTableInfo,
  RawRows,
  ServicePeriod,
  StaffCategory,
  TalentFlow,
  StandardsIndex,
  Summary,
  EligibilityResult,
  PositionHolders,
  PositionQualification,
  VacancyCandidate,
  VacancyCandidateList,
} from '@shared/types';
import type {
  AskAnswer,
  AskTurn,
  CriterionCheckRequest,
  CriterionCheckResult,
  OrgSearchResult,
  OrgTreeNode,
  PersonProfile,
  RotationReview,
  RotationReviewComputed,
  RotationReviewItem,
  UnitView,
} from '@shared/views';

/**
 * ตัวเรียก API ตัวเดียวของทั้งแอป
 * ตอน dev Vite proxy /api ไปที่ server ที่พอร์ต 4000 ให้แล้ว จึงเรียกด้วย path สัมพัทธ์ได้เลย
 */
/*
 * อ่านผ่านตัวแปรกลางแบบกันพัง เพราะ import.meta.env มีเฉพาะตอน Vite bundle ให้
 * สคริปต์ที่รันไฟล์นี้ตรง ๆ ด้วย Node/tsx (เช่น scripts/smoke-render.tsx) จะได้ env เป็น undefined
 * ถ้าเขียน import.meta.env.VITE_API_BASE ตรง ๆ จะพังตั้งแต่ตอนโหลดโมดูล ก่อนเรนเดอร์ด้วยซ้ำ
 */
const ENV = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
const BASE = ENV?.VITE_API_BASE ?? '';

/**
 * โยนเมื่อเซิร์ฟเวอร์ตอบ 401 — แปลว่ายังไม่ได้เข้าสู่ระบบหรือเซสชันหมดอายุ
 * App จับตัวนี้แล้วพากลับไปหน้าเข้าสู่ระบบ
 */
export class NotLoggedInError extends Error {
  constructor() {
    super('ยังไม่ได้เข้าสู่ระบบ หรือเซสชันหมดอายุแล้ว');
    this.name = 'NotLoggedInError';
  }
}

/** ผู้ใช้ที่เข้าสู่ระบบอยู่ */
export interface SessionUser {
  username: string;
  displayName: string;
  role: string;
  /**
   * มุมมองข้อมูลของบัญชีนี้ — ไม่มี = เห็นทุกอัตรา
   *
   * ⚠ ถ้ามีค่า แปลว่าตัวเลขทุกตัวบนหน้าจอถูกตัดให้แคบลงแล้วตั้งแต่ฝั่งเซิร์ฟเวอร์
   *   หน้าจอ *ต้อง* บอกผู้ใช้ด้วย viewLabel/viewNote ห้ามแสดงยอดที่ถูกกรองเงียบ ๆ
   *   เพราะยอดที่ต่างจากที่เคยเห็นโดยไม่มีคำอธิบาย อ่านออกได้ว่าระบบคำนวณผิด
   */
  view?: string;
  viewLabel?: string;
  viewNote?: string;
}

async function request<T>(
  path: string,
  init?: RequestInit,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  const url = new URL(`${BASE}/api${path}`, window.location.origin);
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
  }

  const res = await fetch(url, {
    ...init,
    // เซสชันอยู่ใน cookie แบบ httpOnly จึงต้องบอก fetch ให้ส่ง cookie ไปด้วยทุกครั้ง
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });

  // 401 จากหน้าอื่น = เซสชันหมดอายุ ให้พากลับไปหน้าเข้าสู่ระบบ
  // แต่ 401 จาก /auth/login เองคือ "รหัสผ่านผิด" ต้องปล่อยข้อความจากเซิร์ฟเวอร์ผ่านไปให้หน้า login แสดง
  if (res.status === 401 && !path.startsWith('/auth/')) throw new NotLoggedInError();

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let message = body;
    try {
      const parsed = JSON.parse(body) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      /* ไม่ใช่ JSON ก็ใช้ข้อความดิบไป */
    }
    throw new ApiError(res.status, message, `เรียก ${url.pathname} ไม่สำเร็จ (${res.status}) ${message}`.trim());
  }
  return (await res.json()) as T;
}

/**
 * error ที่พก HTTP status ติดมาด้วย
 *
 * มีไว้เพื่อให้ฝั่งเรียกแยก "ไม่มีของชิ้นนี้" (404) ออกจาก "ระบบพัง" (500) ได้
 * ก่อนหน้านี้ทุกอย่างถูกโยนเป็น Error ธรรมดา หน้าจอจึงขึ้นข้อความสีแดงเหมือนกันหมด
 * ทั้งกรณีที่ระบบพังจริง และกรณีที่คำตอบคือ "ยังไม่มีเอกสารของสายงานนี้"
 * ซึ่งเป็นคำตอบที่ถูกต้องและควรอ่านเป็นข้อความปกติ ไม่ใช่ error
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** ข้อความจากเซิร์ฟเวอร์ล้วน ๆ ไม่มี prefix เรื่อง URL */
    readonly serverMessage: string,
    full: string,
  ) {
    super(full);
    this.name = 'ApiError';
  }
}

/** ผลลัพธ์แทน EligibilityReport เมื่อระบบยังไม่มีมาตรฐานของระดับ/สายงานนั้น */
export interface NoStandard {
  noStandard: true;
  /** เหตุผลจากเซิร์ฟเวอร์ — อธิบายแล้วว่าขาดทั้งระดับ หรือขาดเฉพาะสายงานนี้ */
  reason: string;
}

export function isNoStandard(v: unknown): v is NoStandard {
  return typeof v === 'object' && v !== null && (v as NoStandard).noStandard === true;
}

function get<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  return request<T>(path, undefined, params);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  // ถ้าไม่มี body ห้ามใส่ Content-Type: application/json
  // ไม่งั้น Fastify จะพยายาม parse body ที่ว่างเปล่าแล้วตอบ 400 กลับมา
  return request<T>(
    path,
    body === undefined
      ? { method: 'POST' }
      : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  );
}

/** ผังองค์กรแบบไม่มีรายชื่อ (เบากว่า ใช้วาดผัง) */
export type ShallowOrgNode = Omit<OrgNode, 'positions'>;

export const api = {
  /* ---------------- เข้าสู่ระบบ ---------------- */
  /** ใครเข้าอยู่ตอนนี้ — คืน null ถ้ายังไม่ได้เข้า (ไม่โยน error) */
  me: () => get<{ user: SessionUser | null }>('/auth/me').then((r) => r.user),
  login: (username: string, password: string) =>
    post<{ user: SessionUser }>('/auth/login', { username, password }).then((r) => r.user),
  logout: () => post<{ ok: true }>('/auth/logout'),

  summary: () => get<Summary>('/summary'),
  orgTree: () => get<ShallowOrgNode[]>('/org-tree'),
  orgTreeFull: () => get<OrgNode[]>('/org-tree', { detail: 'full' }),
  vacancies: (scope: 'senior' | 'all' = 'senior') => get<VacancyList>('/vacancies', { scope }),
  /**
   * คลื่นเกษียณรายปีงบประมาณ — ส่ง unitPath เพื่อเอาเฉพาะกิ่งหนึ่งของผัง
   *
   * ⚠ unitPath ต้องเป็นรหัสโหนดจากผังตรง ๆ (OrgNode.id) ห้ามส่งชื่อหน่วยที่พิมพ์เอง
   *   ฝั่งเซิร์ฟเวอร์เทียบเส้นทางแบบตรงตัว ชื่อที่พิมพ์เองจะไม่ match สักแถว
   *   แล้วกราฟจะว่างเปล่าโดยไม่มี error — ถ้าเป็นชื่อที่คนพิมพ์ ให้ส่ง unit แทน
   */
  retirement: (years = 5, unitPath?: string) =>
    get<RetirementBucket[]>('/retirement', { years, unitPath }),
  /** รายชื่อผู้เกษียณรายคน — ตอบ "ใครจะเกษียณเมื่อไหร่" ไม่ใช่แค่ "กี่คน" */
  retirementRoster: (years = 5) => get<RetirementRoster>('/retirement/roster', { years }),
  /** แผนหมุนเวียนกำลังคนแนวราบ — อัตราว่างคู่กับผู้ที่อยู่สายงานและระดับเดียวกันในหน่วยอื่น */
  rotation: () => get<RotationPlan>('/rotation'),
  /** ผู้เข้าข่ายของอัตราว่างหนึ่งอัตรา — เกณฑ์ตามมาตรฐาน (ถ้ามี) + ผู้ที่ย้ายแนวราบมาได้ */
  vacancyCandidates: (key: string, sort?: CandidateOrder) =>
    get<VacancyCandidateList>(`/positions/${encodeURIComponent(key)}/candidates`, { sort }),
  /**
   * ผู้ผ่านเกณฑ์ระยะเวลาของอัตราหนึ่ง แบ่งหน้าและค้นที่เซิร์ฟเวอร์
   *
   * ⚠ ตอนนี้ไม่มีหน้าจอไหนเรียกแล้ว — ปุ่ม "แสดงรายชื่อทั้งหมด" ในกล่องผู้เข้าเงื่อนไข
   *   ถูกเอาออกตามที่ผู้ใช้สั่ง เหลือหัวรายชื่อห้าคนกับช่องค้นหาคนมาเทียบ
   *   (ซึ่งค้นจากผู้ครองอัตราทั้งหมด ไม่ใช่จากรายชื่อผู้ผ่านเกณฑ์ ดู VacancyCandidates.tsx)
   *
   *   เก็บไว้เพราะ endpoint ฝั่งเซิร์ฟเวอร์ยังอยู่และยังเป็นทางเดียวที่ค้น "เฉพาะผู้ผ่านเกณฑ์"
   *   ได้จากทั้งชุด ถ้าวันหนึ่งต้องการรายชื่อเต็มอีกครั้ง ให้ใช้ตัวนี้
   *   ไม่ใช่ดึงทั้งหมดมากรองที่หน้าจอ — บางอัตรามีผู้ผ่านเกณฑ์พันกว่าคน
   */
  eligibleCandidates: (
    key: string,
    params: { q?: string; limit?: number; offset?: number; sort?: CandidateOrder },
  ) =>
    get<Paged<EligibilityResult>>(`/positions/${encodeURIComponent(key)}/candidates/eligible`, params),
  /** ผู้ที่ย้ายแนวราบมาได้ของอัตราหนึ่ง แบ่งหน้าและค้นที่เซิร์ฟเวอร์ — ช่องค้นหาของกลุ่มแนวราบใช้ตัวนี้ */
  lateralCandidates: (key: string, params: { q?: string; limit?: number; offset?: number }) =>
    get<Paged<VacancyCandidate>>(`/positions/${encodeURIComponent(key)}/candidates/lateral`, params),
  /**
   * เทียบผู้เข้าเงื่อนไขหลายคนของอัตราเดียว
   *
   * ⚠ ส่ง ids ตามลำดับที่ผู้ใช้ติ๊ก และเซิร์ฟเวอร์ไม่เรียงใหม่
   *   ลำดับคอลัมน์บนหน้าจอจึงเป็นลำดับของผู้ใช้ ไม่ใช่ลำดับที่ระบบจัดให้
   *   ห้าม sort ตรงนี้เพื่อให้ queryKey นิ่ง — ให้ส่วนที่เรียกทำ key ให้นิ่งเอง
   */
  compareCandidates: (key: string, ids: readonly string[]) =>
    get<CandidateComparison>(`/positions/${encodeURIComponent(key)}/candidates/compare`, {
      ids: ids.join(','),
    }),
  /** เงื่อนไขของอัตราหนึ่ง (ตัวเกณฑ์) — ต่างจาก vacancyCandidates ซึ่งตอบเป็นรายชื่อคน */
  positionQualification: (key: string) =>
    get<PositionQualification>(`/positions/${encodeURIComponent(key)}/qualification`),
  /** ใครเคยครองอัตรานี้บ้าง — อ่านจากตารางประวัติ ไม่ใช่จากทะเบียนตำแหน่ง */
  positionHolders: (key: string) =>
    get<PositionHolders>(`/positions/${encodeURIComponent(key)}/holders`),
  positions: (params: {
    status?: 'all' | 'vacant' | 'occupied' | 'retiring';
    category?: StaffCategory;
    level?: string;
    unit?: string;
    /** เส้นทางหน่วยงานแบบตรงตัว รวมหน่วยย่อยทุกชั้น — ใช้เมื่อยอดต้องกระทบกับการ์ดในผัง */
    unitPath?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }) => get<Paged<Position>>('/positions', params),
  history: (personaId: string) =>
    get<ServicePeriod[]>(`/people/${encodeURIComponent(personaId)}/history`),

  /* ---------------- ชั้นกฎ ---------------- */
  standards: () => get<StandardsIndex>('/rules/standards'),
  /**
   * ผู้ผ่านเกณฑ์ระยะเวลาของระดับที่ระบุ
   *
   * ⚠ ต้องส่ง `line` ทุกครั้งที่รู้สายงาน ห้ามเรียกด้วยระดับเปล่า ๆ
   *   ประเภทวิชาการ (ชำนาญการพิเศษ · เชี่ยวชาญ · ทรงคุณวุฒิ) ใช้เอกสารคนละฉบับต่อสายงาน
   *   เรียกโดยไม่มี line จะได้ 404 เสมอ เพราะไม่มีฉบับไหนครอบทุกสายงานในระดับนั้น
   *
   * 404 ไม่ถือเป็น error — คืน NoStandard พร้อมเหตุผลจากเซิร์ฟเวอร์ให้หน้าจอแสดงเป็นข้อความปกติ
   */
  eligibility: (dpisLevel: string, line?: string | null): Promise<EligibilityReport | NoStandard> =>
    get<EligibilityReport>(`/eligibility/${encodeURIComponent(dpisLevel)}`, {
      line: line?.trim() || undefined,
    }).catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) {
        return { noStandard: true as const, reason: e.serverMessage };
      }
      throw e;
    }),
  seniority: (level: string) => get<SeniorityList>(`/seniority/${encodeURIComponent(level)}`),
  authority: () => get<AuthorityMatrix>('/rules/authority'),
  /** บัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้งตามมาตรา 57 */
  executives: () => get<ExecutiveRoster>('/executives'),
  /**
   * ผังคนระดับ 9 ขึ้นไป โดยมีปลัดกระทรวงเป็นราก
   *
   * ⚠ คนละอย่างกับ executives() ซึ่งเป็นบัญชีแบนตามมาตรา 57
   *   อันนี้เป็นผัง ขอบเขตคือระดับ 9 ขึ้นไป ไม่ใช่ขอบเขตของมาตรา 57
   */
  executiveTree: () => get<ExecutiveTree>('/executives/tree'),
  /** ผังผู้บริหารตามตำแหน่ง — ปลัด → รองปลัดรายกลุ่มภารกิจ → ส่วนราชการ → กล่องรายตำแหน่ง */
  executivePositions: () => get<ExecutivePositionsView>('/executives/positions'),
  /** อัตราเดียวตาม posId — ใช้ตอนกดการ์ดคนในผัง เพื่อเปิดรายละเอียดอัตรานั้น */
  position: (key: string) => get<Position>(`/positions/${encodeURIComponent(key)}`),

  /* ---------------- ข้อมูลดิบจากไฟล์ (ไว้เปิดเทียบ) ---------------- */
  rawTables: () => get<{ tables: RawTableInfo[] }>('/raw').then((r) => r.tables),
  rawRows: (table: string, params: { q?: string; limit?: number; offset?: number }) =>
    get<RawRows>(`/raw/${encodeURIComponent(table)}`, params),

  /* ---------------- เส้นทางความก้าวหน้า ---------------- */
  careerGroups: () => get<{ groups: CareerGroup[] }>('/career-path').then((r) => r.groups),
  careerGroup: (groupId: string) =>
    get<CareerGroupOverview>(`/career-path/group/${encodeURIComponent(groupId)}`),
  careerPathOf: (personaId: string) =>
    get<CareerPathForPerson>(`/career-path/person/${encodeURIComponent(personaId)}`),
  /** คู่ (สายงาน, ระดับ) ที่มีผังรองรับ — ผังหน่วยงานใช้ตัดสินว่าจะติดไอคอนแถวไหน */
  careerCoverage: () => get<CareerCoverage>('/career-path/coverage'),
  /** เส้นทางของ "ตำแหน่ง" — ขึ้นกับสายงานกับระดับ ไม่ขึ้นกับเลขที่ตำแหน่ง (ซึ่งซ้ำกันได้) */
  careerPathOfPosition: (line: string, level: string) =>
    get<PositionCareerPath>('/career-path/position', { line, level }),

  /* ---------------- โครงสร้าง/ประวัติ (ไล่ลงชั้น) ---------------- */
  /** ผังทั้งต้นสำหรับแคนวาส — ไม่มีรายชื่อบุคคลติดมาด้วย */
  orgChart: () => get<{ root: OrgTreeNode; source: string }>('/org/tree'),
  /** ค้นหาหน่วยงานจากชื่อหน่วยงานหรือชื่อบุคลากร — คืนเฉพาะรหัสหน่วยงานที่ตรง */
  orgSearch: (q: string) => get<OrgSearchResult>('/org/search', { q }),
  /** หน่วยงานหนึ่งหน่วย พร้อมหน่วยย่อยและรายชื่อที่สังกัดโดยตรง */
  unit: (unitId: string) => get<UnitView>(`/org/units/${encodeURIComponent(unitId)}`),
  /** การ์ดประวัติรายบุคคล — ขอทีละคนเท่านั้นโดยตั้งใจ ดูคำอธิบายฝั่งเซิร์ฟเวอร์ */
  profile: (personaId: string) =>
    get<PersonProfile>(`/people/${encodeURIComponent(personaId)}/profile`),

  /* ---------------- ถาม-ตอบด้วย AI ---------------- */
  /** ฟีเจอร์เปิดอยู่ไหม — ถามก่อนเสมอ จะได้ไม่ต้องเสียค่าเรียกโมเดลเพื่อรู้ว่าใช้ไม่ได้ */
  askStatus: () =>
    get<{
      enabled: boolean;
      model: string | null;
      samples: string[];
      topics: string[];
      /** จำนวนคู่คำถาม-คำตอบที่ยอมให้ส่งย้อนหลัง — มาจากเซิร์ฟเวอร์ ห้ามตั้งซ้ำที่หน้าจอ */
      historyLimit: number;
    }>('/ask/status'),
  /**
   * ถามหนึ่งคำถาม — ใช้เวลาหลายวินาที เพราะเซิร์ฟเวอร์ต้องคุยกับโมเดลหลายรอบ
   *
   * history คือคู่คำถาม-คำตอบก่อนหน้าที่หน้าจอเก็บไว้เอง
   * จำนวนสูงสุดมาจาก askStatus().historyLimit ไม่ใช่เลขที่หน้าจอตั้งเอง
   * เซิร์ฟเวอร์ไม่ได้จำบทสนทนาให้ ดูเหตุผลที่ routes/index.ts
   */
  ask: (question: string, history: AskTurn[] = []) =>
    post<AskAnswer>('/ask', { question, history }),

  /**
   * ให้ผู้ช่วยอ่านเหตุผลในร่างรอบโยกย้าย
   *
   * ⚠ ส่งไปเฉพาะถ้อยคำ ไม่ส่ง personaId — ผู้ช่วยตัวนี้อ่านภาษาอย่างเดียว
   *   ไม่ต้องเปิดข้อมูลบุคคลเพิ่ม จึงไม่มีทางดึงคนนอกขอบเขตออกมาได้
   * ⚠ ตัวเลขที่ส่งไปคือค่าที่หน้าจอคำนวณแล้ว ส่งให้ไป "เล่า" ไม่ใช่ให้คิดเอง
   */
  rotationReview: (items: RotationReviewItem[], computed: RotationReviewComputed) =>
    post<RotationReview>('/rotation/review', { items, computed }),

  /**
   * ให้ผู้ช่วยตรวจเกณฑ์เชิงเนื้อความของบัญชีผู้ได้รับการพิจารณาหนึ่งอัตรา
   *
   * ⚠ ส่ง personaId ไปด้วย ต่างจาก rotationReview โดยตั้งใจ
   *   เพราะงานนี้ต้องอ่านประวัติซึ่งอยู่ที่เซิร์ฟเวอร์ และการดึงผ่าน repository()
   *   ทำให้ขอบเขตของบัญชีผู้ใช้บังคับใช้เอง คนนอกขอบเขตได้ประวัติว่าง
   * ⚠ ส่งมาเฉพาะเกณฑ์ที่ตรวจด้วยข้อมูลไม่ได้ — ที่เหลือหน้าจอตอบเองทันที
   */
  rotationCriteria: (body: CriterionCheckRequest) =>
    post<CriterionCheckResult>('/rotation/criteria', body),

  /* ---------------- มุมมองเชิงวางแผน ---------------- */
  heatmap: (years = 5) => get<RetirementHeatmap>('/retirement/heatmap', { years }),
  talentFlow: (years = 5) => get<TalentFlow>('/talent-flow', { years }),
};
